import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { FormDialog } from '@/components/ui/form-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import AddAppointment from '@/components/appointment/AddAppointment';
import CreateAdditionalServicesBill from '@/components/billing/CreateAdditionalServicesBill';
import { Patient, PatientCreateData } from '@/types/types';
import { calculateAge, leadPrefillDobForRegistration, toIstDateInputValue } from '@/utils/dateUtils';
import { patientApi } from '@/api/patient';
import { billingApi } from '@/api/billing';
import { leadsApi, type Lead } from '@/api/leads';
import PatientDetails from './PatientDetails';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import {
  UserPlus,
  Search,
  Phone,
  Calendar,
  Users,
  AlertCircle,
  User as UserIcon,
  Wallet,
} from 'lucide-react';
import { Description, DialogTitle } from '@radix-ui/react-dialog';
import { mapCrmLeadSourceToRegistration } from '@/lib/leadRegistrationSource';
import { leadSourceNeedsDetails } from '@/lib/leadSources';
import { LeadSourceSearchableSelect } from '@/components/common/LeadSourceSearchableSelect';

// Registration mode enum (sources: shared CANONICAL_LEAD_SOURCES)
const REGISTRATION_MODES = [
  { value: 'OPD', label: 'OPD' },
  { value: 'IPD', label: 'IPD' },
  { value: 'EMERGENCY', label: 'Emergency' },
];

function patientDueBalancesPath(role: string | undefined) {
  if (role === 'RECEPTIONIST' || role === 'OPERATIONS_MANAGER') return '/receptionist/patient-due-balances';
  if (role === 'SUPER_ADMIN') return '/super-admin/patient-due-balances';
  return '/hospital-admin/patient-due-balances';
}

export default function Patients() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const leadConversionPendingRef = useRef(false);
  /** After CRM "OPD Booked" flow on hospital-admin: open Add Appointment once patient is created. */
  const leadConversionOpenApptRef = useRef(false);
  const [crmAutoAppointmentPatientId, setCrmAutoAppointmentPatientId] = useState<string | null>(null);
  const [crmAutoAppointmentSource, setCrmAutoAppointmentSource] = useState<{
    registrationSource?: string | null;
    registrationSourceDetails?: string | null;
  } | null>(null);
  /** CRM lead id when registering from Converted + OPD Booked (passed through to Add Appointment). */
  const [crmOpdBookingLeadId, setCrmOpdBookingLeadId] = useState<string | null>(null);
  const [crmLeadHospitalId, setCrmLeadHospitalId] = useState<string | null>(null);
  const [additionalServicesPatient, setAdditionalServicesPatient] = useState<Patient | null>(null);
  const allowedAddRoles = ['HOSPITAL_ADMIN', 'RECEPTIONIST', 'OPERATIONS_MANAGER', 'SALES_PERSON', 'SALES_MANAGER'];
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState<string>("");
  const [searchValue, setSearchValue] = useState('');
  const [, setSearchResult] = useState<Patient | null>(null);
  const [searchResults, setSearchResults] = useState<Patient[]>([]);
  const [addName, setAddName] = useState('');
  const [addPhone, setAddPhone] = useState('');
  const [addGender, setAddGender] = useState('');
  const [addDob, setAddDob] = useState('');
  const [addRegistrationMode, setAddRegistrationMode] = useState('OPD');
  const [addRegistrationSource, setAddRegistrationSource] = useState('WALK_IN');
  const [addRegistrationSourceDetails, setAddRegistrationSourceDetails] = useState('');
  const queryClient = useQueryClient();
  const [patientExists, setPatientExists] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchAttempted, setSearchAttempted] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  const { data: patients, isLoading } = useQuery<Patient[]>({
    queryKey: ['hospital-patients'],
    queryFn: async () => {
      const response = await patientApi.getAllPatients();
      return response;
    },
  });

  const searchPatientIds = useMemo(
    () => searchResults.map((p) => p.id),
    [searchResults]
  );

  const { data: dueByPatientId = {} } = useQuery({
    queryKey: ['patient-due-summary', searchPatientIds],
    queryFn: () => billingApi.getPatientDueBalanceSummary(searchPatientIds),
    enabled: patientExists && searchPatientIds.length > 0,
  });

  const navigateToOpdBookingAfterLead = useCallback(
    (
      patient: Patient,
      opts?: { crmLeadId?: string | null; leadHospitalId?: string | null }
    ) => {
      const openApptAfter = leadConversionOpenApptRef.current;
      leadConversionPendingRef.current = false;
      leadConversionOpenApptRef.current = false;
      setIsAddDialogOpen(false);
      const crmLeadId = opts?.crmLeadId ?? crmOpdBookingLeadId;
      const leadHospitalId = opts?.leadHospitalId ?? crmLeadHospitalId;
      const apptState = {
        openAddAppointment: true,
        patientId: patient.id,
        registrationSource: patient.registrationSource,
        registrationSourceDetails: patient.registrationSourceDetails ?? null,
        ...(crmLeadId ? { crmLeadId } : {}),
        ...(leadHospitalId ? { leadHospitalId } : {}),
      };
      if (user?.role === 'SALES_PERSON' || user?.role === 'SALES_MANAGER') {
        toast.success('Patient found — book an appointment');
        navigate('/sales-person/appointments', { state: apptState });
        return;
      }
      if (user?.role === 'RECEPTIONIST' || user?.role === 'OPERATIONS_MANAGER') {
        toast.success('Patient found — book an appointment');
        navigate('/receptionist/appointments', { state: apptState });
        return;
      }
      if (openApptAfter && user?.role === 'HOSPITAL_ADMIN') {
        toast.success('Patient found — book OPD appointment');
        navigate('/hospital-admin/appointments', { state: apptState });
      }
    },
    [navigate, user?.role, crmOpdBookingLeadId, crmLeadHospitalId]
  );

  /** Merge CRM lead with optional chain patient — never drop lead age/gender/DOB when patient row is incomplete. */
  const applyCrmConversionPrefill = useCallback(
    (lead: Partial<Lead>, existingPatient?: Patient | null) => {
      const name = existingPatient?.name?.trim() || lead.patientName?.trim() || '';
      if (name) setAddName(name);

      const raw = String(existingPatient?.phone || lead.phone || '').replace(/\D/g, '');
      const digits = raw.slice(-10);
      if (digits.length === 10) setAddPhone(digits);
      else if (raw.length > 0) setAddPhone(raw.slice(0, 10));

      const leadGender =
        lead.gender === 'MALE' || lead.gender === 'FEMALE' || lead.gender === 'OTHER'
          ? lead.gender
          : '';
      const patientGender =
        existingPatient?.gender === 'MALE' ||
        existingPatient?.gender === 'FEMALE' ||
        existingPatient?.gender === 'OTHER'
          ? existingPatient.gender
          : '';
      setAddGender(patientGender || leadGender);

      const dobFromPatient = existingPatient?.dob
        ? toIstDateInputValue(existingPatient.dob)
        : '';
      const dobFromLead = leadPrefillDobForRegistration({ dob: lead.dob, age: lead.age });
      setAddDob(dobFromPatient || dobFromLead);

      if (existingPatient?.registrationSource) {
        setAddRegistrationSource(String(existingPatient.registrationSource));
        setAddRegistrationSourceDetails(existingPatient.registrationSourceDetails?.trim() || '');
      } else {
        const mapped = mapCrmLeadSourceToRegistration(lead.source);
        setAddRegistrationSource(mapped.registrationSource);
        setAddRegistrationSourceDetails(mapped.registrationSourceDetails);
      }
      setAddRegistrationMode(
        existingPatient?.registrationMode ? String(existingPatient.registrationMode) : 'OPD'
      );
    },
    []
  );

  /** CRM: lead CONVERTED + OPD Booked → land here with registration form open (then book OPD for admin) */
  useEffect(() => {
    const st = location.state as {
      fromLeadConversion?: boolean;
      crmLeadId?: string;
      leadHospitalId?: string;
      openOpdAppointmentAfterPatient?: boolean;
      patientName?: string;
      phone?: string;
      email?: string;
      leadSource?: string;
      gender?: Lead['gender'];
      dob?: string | null;
      age?: number | null;
    } | null;
    if (!st?.fromLeadConversion) return;

    const leadId = st.crmLeadId ? String(st.crmLeadId) : null;
    setCrmOpdBookingLeadId(leadId);
    setCrmLeadHospitalId(st.leadHospitalId ? String(st.leadHospitalId) : null);
    leadConversionPendingRef.current = true;
    leadConversionOpenApptRef.current = !!st.openOpdAppointmentAfterPatient;
    navigate(location.pathname, { replace: true, state: {} });

    void (async () => {
      let lead: Partial<Lead> = {
        patientName: st.patientName,
        phone: st.phone,
        source: st.leadSource,
        gender: st.gender,
        dob: st.dob,
        age: st.age ?? undefined,
      };

      if (leadId) {
        try {
          const res = await leadsApi.getLeadById(leadId);
          const fetched = res.data.data as Partial<Lead>;
          lead = {
            ...fetched,
            gender: fetched.gender ?? st.gender,
            dob: fetched.dob ?? st.dob ?? undefined,
            age: fetched.age ?? st.age ?? undefined,
          };
        } catch {
          /* use navigation state */
        }
      }

      let chainPatient: Patient | null = null;
      const digits = String(lead.phone || st.phone || '')
        .replace(/\D/g, '')
        .slice(-10);
      if (digits.length === 10) {
        try {
          const existing = await patientApi.getPatientByPhone(digits);
          const atThisHospital = existing.find((p) => p.hospitalId === user?.hospitalId);
          if (existing.length > 0 && leadConversionPendingRef.current) {
            const forBooking = atThisHospital ?? existing[0];
            navigateToOpdBookingAfterLead(forBooking, {
              crmLeadId: leadId,
              leadHospitalId: st.leadHospitalId ? String(st.leadHospitalId) : null,
            });
            return;
          }
          chainPatient = existing[0] ?? null;
        } catch {
          /* new patient — lead-only prefill */
        }
      }

      applyCrmConversionPrefill(lead, chainPatient);

      setIsAddDialogOpen(true);
      toast.info('Patient details prefilled from CRM — confirm and save.');
    })();
  }, [
    location.state,
    location.pathname,
    navigate,
    user?.hospitalId,
    applyCrmConversionPrefill,
    navigateToOpdBookingAfterLead,
  ]);

  const addPatientMutation = useMutation({
    mutationFn: async (data: {
      name: string;
      phone: string;
      gender: string;
      dob: string;
      registrationMode: string;
      registrationSource: string;
      registrationSourceDetails?: string;
      referralPersonName?: string;
    }) => {
      const response = await patientApi.createPatient(data as unknown as PatientCreateData);
      return response;
    },
    onSuccess: (patient) => {
      queryClient.invalidateQueries({ queryKey: ['hospital-patients'] });
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      if ((patient as Patient & { chainExisting?: boolean }).chainExisting) {
        const regName = (patient as Patient & { hospital?: { name?: string } }).hospital?.name;
        toast.info(
          regName
            ? `Patient already registered at ${regName}. You can book visits at any chain hospital.`
            : 'Patient already registered in your hospital chain.'
        );
        setSearchResults([patient]);
        setPatientExists(true);
        setSearchAttempted(true);
        setIsAddDialogOpen(false);
        resetForm();
        if (leadConversionPendingRef.current || crmOpdBookingLeadId) {
          navigateToOpdBookingAfterLead(patient, {
            crmLeadId: crmOpdBookingLeadId,
            leadHospitalId: crmLeadHospitalId,
          });
        }
        return;
      }
      if (leadConversionPendingRef.current) {
        const openApptAfter = leadConversionOpenApptRef.current;
        leadConversionPendingRef.current = false;
        leadConversionOpenApptRef.current = false;
        setIsAddDialogOpen(false);
        resetForm();
        if (user?.role === 'SALES_PERSON' || user?.role === 'SALES_MANAGER') {
          toast.success('Patient registered — book an appointment');
          navigate('/sales-person/appointments', {
            state: {
              openAddAppointment: true,
              patientId: patient.id,
              registrationSource: patient.registrationSource,
              registrationSourceDetails: patient.registrationSourceDetails ?? null,
              ...(crmOpdBookingLeadId ? { crmLeadId: crmOpdBookingLeadId } : {}),
              ...(crmLeadHospitalId ? { leadHospitalId: crmLeadHospitalId } : {}),
            },
          });
          return;
        }
        if (user?.role === 'RECEPTIONIST' || user?.role === 'OPERATIONS_MANAGER') {
          toast.success('Patient registered — book an appointment');
          navigate('/receptionist/appointments', {
            state: {
              openAddAppointment: true,
              patientId: patient.id,
              registrationSource: patient.registrationSource,
              registrationSourceDetails: patient.registrationSourceDetails ?? null,
              ...(crmOpdBookingLeadId ? { crmLeadId: crmOpdBookingLeadId } : {}),
              ...(crmLeadHospitalId ? { leadHospitalId: crmLeadHospitalId } : {}),
            },
          });
          return;
        }
        if (openApptAfter && user?.role === 'HOSPITAL_ADMIN') {
          toast.success('Patient registered — book OPD appointment');
          navigate('/hospital-admin/appointments', {
            state: {
              openAddAppointment: true,
              patientId: patient.id,
              registrationSource: patient.registrationSource,
              registrationSourceDetails: patient.registrationSourceDetails ?? null,
              ...(crmOpdBookingLeadId ? { crmLeadId: crmOpdBookingLeadId } : {}),
              ...(crmLeadHospitalId ? { leadHospitalId: crmLeadHospitalId } : {}),
            },
          });
          return;
        }
      }
      setCrmOpdBookingLeadId(null);
      setIsAddDialogOpen(false);
      resetForm();
      toast.success('Patient added successfully');
    },
    onError: (error) => {
      const ax = error as { response?: { data?: { message?: string } } };
      toast.error(ax.response?.data?.message || 'Failed to add patient');
      console.error('Add patient error:', error);
    },
  });

  const performSearch = useCallback(async (searchTerm: string) => {
    if (!searchTerm || searchTerm.trim() === '') {
      setSearchAttempted(false);
      setSearchResults([]);
      setPatientExists(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      let res;
      if (searchTerm.length === 10 && !isNaN(Number(searchTerm))) {
        res = await patientApi.getPatientByPhone(searchTerm);
        if (res.length > 0) {
          setSearchResults(res);
          setPatientExists(true);
          setSearchAttempted(true);
          setIsSearching(false);
          setAddPhone(searchTerm);
        } else {
          setSearchResults([]);
          setPatientExists(false);
          setSearchAttempted(true);
          setIsSearching(false);
          setAddPhone(searchTerm);
        }
      } else {
        res = await patientApi.getPatientByName(searchTerm);
        if (res.length > 0) {
          setSearchResults(res);
          setPatientExists(true);
          setSearchAttempted(true);
          setIsSearching(false);
        } else {
          setSearchResults([]);
          setPatientExists(false);
          setSearchAttempted(true);
          setAddName(searchTerm);
          setIsSearching(false);
        }
        setSearchResult(null);
      }
    } catch (err) {
      setSearchResult(null);
      setSearchResults([]);
      setPatientExists(false);
      if (searchTerm.length === 10 && !isNaN(Number(searchTerm))) {
        setAddPhone(searchTerm);
      } else {
        setAddName(searchTerm);
      }
      setSearchAttempted(true);
    } finally {
    }
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchValue.trim().length >= 2) { // Only search if at least 2 characters
        performSearch(searchValue.trim());
      } else if (searchValue.trim().length === 0) {
        // Clear results when search is empty
        setSearchAttempted(false);
        setSearchResults([]);
        setPatientExists(false);
        setIsSearching(false);
      }
    }, 500); // 500ms debounce delay

    return () => clearTimeout(timeoutId);
  }, [searchValue, performSearch]);


  /** HTML date input uses YYYY-MM-DD. Slash dates may be DD/MM (IN) or MM/DD (US); avoid month>12 bugs. */
  const parseDobToIso = (dob: string): string => {
    const raw = (dob || '').trim();
    if (!raw) return new Date('1990-01-01').toISOString();
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const d = new Date(`${raw}T12:00:00`);
      if (Number.isNaN(d.getTime())) throw new Error('Invalid date of birth');
      return d.toISOString();
    }
    if (raw.includes('/')) {
      const parts = raw.split('/').map((p) => p.trim());
      if (parts.length !== 3) throw new Error('Invalid date of birth');
      const a = parseInt(parts[0], 10);
      const b = parseInt(parts[1], 10);
      const y = parseInt(parts[2], 10);
      if ([a, b, y].some((n) => Number.isNaN(n)) || y < 1900 || y > 2100) {
        throw new Error('Invalid date of birth');
      }
      let month: number;
      let day: number;
      if (a > 12) {
        day = a;
        month = b;
      } else if (b > 12) {
        month = a;
        day = b;
      } else {
        month = a;
        day = b;
      }
      const d = new Date(y, month - 1, day);
      if (Number.isNaN(d.getTime())) throw new Error('Invalid date of birth');
      return d.toISOString();
    }
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) throw new Error('Invalid date of birth');
    return d.toISOString();
  };

  const handleAddSubmit = () => {
    // Phone validation: must be exactly 10 digits
    if (!/^\d{10}$/.test(addPhone)) {
      setPhoneError('Phone number must be exactly 10 digits');
      return;
    } else {
      setPhoneError('');
    }
    try {
      addPatientMutation.mutate({
        name: addName,
        phone: addPhone,
        gender: addGender,
        dob: parseDobToIso(addDob),
        registrationMode: addRegistrationMode,
        registrationSource: addRegistrationSource,
        registrationSourceDetails:
          addRegistrationSource === 'REFERRAL'
            ? undefined
            : addRegistrationSourceDetails.trim()
              ? addRegistrationSourceDetails.trim()
              : undefined,
        referralPersonName:
          addRegistrationSource === 'REFERRAL'
            ? (addRegistrationSourceDetails.trim() || undefined)
            : undefined,
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Invalid date of birth');
    }
  };

  // Helper to reset form fields
  const resetForm = () => {
    setAddName('');
    setAddPhone('');
    setAddGender('');
    setAddDob('');
    setAddRegistrationMode('OPD');
    setAddRegistrationSource('WALK_IN');
    setAddRegistrationSourceDetails('');
    setSearchValue('');
    setSearchResult(null);
    setPatientExists(false);
    setPhoneError('');
    setSearchAttempted(false);
    setIsSearching(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-pulse">
          <div className="text-lg text-gray-600">Loading patients...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] bg-artis-cream p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="thms-page-title flex items-center gap-2">
              <Users className="h-8 w-8 text-blue-600" />
              Patient Management
            </h1>
            <p className="thms-page-subtitle">Manage and track all patient records</p>
          </div>
          {allowedAddRoles.includes(user?.role || '') && (
            <Button
              onClick={() => setIsAddDialogOpen(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg"
              size="lg"
            >
              <UserPlus className="mr-2 h-5 w-5" />
              Add New Patient
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-artis-slate">Total Patients</p>
                  <p className="text-2xl font-bold tabular-nums text-artis-brown-dark">{patients?.length || 0}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-artis-slate">Recent Registrations</p>
                  <p className="text-2xl font-bold tabular-nums text-artis-brown-dark">
                    {patients?.filter(p => {
                      const today = new Date();
                      const patientDate = new Date(p.createdAt || '');
                      return today.toDateString() === patientDate.toDateString();
                    }).length || 0}
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <UserPlus className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-artis-slate">Emergency Patients</p>
                  <p className="text-2xl font-bold tabular-nums text-artis-brown-dark">
                    {patients?.filter(p => p.registrationMode === 'EMERGENCY').length || 0}
                  </p>
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Search Patients
            </CardTitle>
            <CardDescription>
              Search patients across your hospital chain — enter a 10-digit phone number or patient name
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col md:flex-row gap-3">
              <Input
                placeholder="Enter phone number (10 digits) or patient name"
                value={searchValue}
                onChange={e => setSearchValue(e.target.value)}
                className="flex-1"
              />
            </div>

            {/* Loading indicator */}
            {isSearching && searchValue.trim().length >= 2 && (
              <div className="flex items-center gap-2 text-blue-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="text-sm">Searching...</span>
              </div>
            )}

            {/* Search Results */}
            {patientExists && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 font-semibold text-amber-800 mb-3">
                  <span className="text-sm">Search Results</span>
                </div>
                <div className="space-y-3">
                  {searchResults.map((patient) => (
                    <div
                      key={patient.id}
                      className="flex flex-col md:flex-row md:items-center justify-between bg-white border border-amber-200 rounded-lg px-4 py-3 shadow-sm"
                    >
                      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                        <div className="flex items-center gap-2">
                          <UserIcon className="h-5 w-5 text-gray-500" />
                          <span
                            className="font-semibold text-blue-900 text-lg cursor-pointer hover:underline"
                            onClick={() => {
                              setSelectedPatientId(patient.id);
                              setIsDetailsDialogOpen(true);
                            }}
                          >
                            {patient.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <Badge variant="outline">{patient.gender ?? "—"}</Badge>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {patient.dob
                              ? `${calculateAge(patient.dob) ?? "—"} yrs`
                              : "—"}
                          </span>
                          <span className="flex items-center gap-1">
                            <Phone className="h-4 w-4" />
                            {patient.phone}
                          </span>
                          {patient.hospital?.name ? (
                            <Badge variant="secondary" className="text-xs">
                              Registered at {patient.hospital.name}
                            </Badge>
                          ) : null}
                          {(dueByPatientId[patient.id] ?? 0) > 0.01 ? (
                            <Badge
                              variant="outline"
                              className="text-xs border-amber-600 text-amber-900 bg-amber-50 font-semibold"
                            >
                              Package due: ₹
                              {dueByPatientId[patient.id].toLocaleString('en-IN', {
                                maximumFractionDigits: 2,
                              })}
                            </Badge>
                          ) : null}
                        </div>
                      </div>
                      <div className="mt-3 md:mt-0 flex flex-wrap items-center gap-2">
                        <AddAppointment
                          patientId={patient.id}
                          crmOpdBookingLeadId={crmOpdBookingLeadId}
                          leadHospitalId={crmLeadHospitalId}
                          onCrmOpdBookingComplete={() => {
                            setCrmOpdBookingLeadId(null);
                            setCrmLeadHospitalId(null);
                          }}
                          bookingHospitalId={user?.role === 'SUPER_ADMIN' ? user.hospitalId : undefined}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          className="border-blue-800 text-blue-800 hover:bg-blue-50"
                          onClick={() => setAdditionalServicesPatient(patient)}
                        >
                          Additional Services
                        </Button>
                        {(dueByPatientId[patient.id] ?? 0) > 0.01 ? (
                          <Button
                            type="button"
                            variant="outline"
                            className="border-amber-700 text-amber-900 hover:bg-amber-50"
                            onClick={() =>
                              navigate(
                                `${patientDueBalancesPath(user?.role)}?patientId=${patient.id}`
                              )
                            }
                          >
                            <Wallet className="h-4 w-4 mr-1" />
                            Collect package due
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-3 border-t border-amber-200">
                  <p className="text-sm text-amber-700">
                    <Button variant="outline" onClick={() => setIsAddDialogOpen(true)}>Add New Patient</Button>
                  </p>
                </div>
              </div>
            )}



            {searchAttempted && !patientExists && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2 text-blue-800">
                  <AlertCircle className="h-5 w-5" />
                  <span className="font-medium">No patient found. You can add a new patient below.</span>
                  <Button variant="outline" onClick={() => setIsAddDialogOpen(true)}>Add New Patient</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Patient Form */}
        <FormDialog
          isOpen={isAddDialogOpen}
          onClose={() => {
            setIsAddDialogOpen(false);
            leadConversionPendingRef.current = false;
            leadConversionOpenApptRef.current = false;
            resetForm();
          }}
          title="Add New Patient"
          onSubmit={handleAddSubmit}
          isLoading={addPatientMutation.isPending}
          showSubmitButton={false}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="add-name">Full Name *</Label>
              <Input
                id="add-name"
                value={addName}
                onChange={e => setAddName(e.target.value)}
                placeholder="Enter patient's full name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="add-phone">Phone Number *</Label>
              <Input
                id="add-phone"
                value={addPhone}
                onChange={e => setAddPhone(e.target.value.replace(/\D/g, ''))}
                maxLength={10}
                placeholder="Enter 10-digit phone number"
                required
              />
              {phoneError && (
                <div className="flex items-center gap-1 text-sm text-red-500">
                  <AlertCircle className="h-4 w-4" />
                  {phoneError}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="add-gender">Gender *</Label>
              <Select value={addGender} onValueChange={setAddGender} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MALE">Male</SelectItem>
                  <SelectItem value="FEMALE">Female</SelectItem>
                  <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="add-dob">Date of Birth *</Label>
              <Input
                id="add-dob"
                type="date"
                value={addDob}
                onChange={e => setAddDob(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="add-registration-mode">Registration Mode *</Label>
              <Select value={addRegistrationMode} onValueChange={setAddRegistrationMode} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select registration mode" />
                </SelectTrigger>
                <SelectContent>
                  {REGISTRATION_MODES.map((mode) => (
                    <SelectItem key={mode.value} value={mode.value}>
                      {mode.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="add-registration-source">Registration Source *</Label>
              <LeadSourceSearchableSelect
                id="add-registration-source"
                value={addRegistrationSource}
                onValueChange={(val) => {
                  setAddRegistrationSource(val);
                  if (!leadSourceNeedsDetails(val)) {
                    setAddRegistrationSourceDetails('');
                  }
                }}
                placeholder="Select registration source"
                searchPlaceholder="Search registration source…"
              />
              {leadConversionPendingRef.current && (
                <p className="text-xs text-muted-foreground">
                  Prefilled from CRM lead — update any field if needed before saving.
                </p>
              )}
            </div>

            {leadSourceNeedsDetails(addRegistrationSource) && (
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="add-registration-source-details">
                  {addRegistrationSource === 'REFERRAL'
                    ? 'Referral Details *'
                    : addRegistrationSource === 'AFFILIATE'
                      ? 'Affiliate Details *'
                      : addRegistrationSource === 'OTHER'
                        ? 'Other Source *'
                        : 'Custom Source *'}
                </Label>
                <Input
                  id="add-registration-source-details"
                  value={addRegistrationSourceDetails}
                  onChange={e => setAddRegistrationSourceDetails(e.target.value)}
                  placeholder={
                    addRegistrationSource === 'REFERRAL'
                      ? 'Enter referral details'
                      : addRegistrationSource === 'AFFILIATE'
                        ? 'Enter affiliate details'
                        : 'Enter custom source (e.g. CRM lead source)'
                  }
                  required
                />
              </div>
            )}
            {addRegistrationSourceDetails.trim() &&
              addRegistrationSource !== 'REFERRAL' &&
              addRegistrationSource !== 'AFFILIATE' &&
              addRegistrationSource !== 'CUSTOM' && (
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="add-registration-source-note">CRM source note</Label>
                <Input
                  id="add-registration-source-note"
                  value={addRegistrationSourceDetails}
                  onChange={e => setAddRegistrationSourceDetails(e.target.value)}
                  placeholder="Original CRM lead source"
                />
              </div>
            )}
          </div>

          <Separator className="my-4" />

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsAddDialogOpen(false);
                leadConversionPendingRef.current = false;
                leadConversionOpenApptRef.current = false;
                setCrmOpdBookingLeadId(null);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleAddSubmit}
              disabled={addPatientMutation.isPending || !addName || !addPhone || !addGender || !addDob}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {addPatientMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Adding Patient...
                </>
              ) : (
                <>
                  <UserPlus className="mr-2 h-4 w-4" />
                  Add Patient
                </>
              )}
            </Button>
          </div>
        </FormDialog>
        {/* Patient Details Dialog */}
        <Dialog open={isDetailsDialogOpen} onOpenChange={setIsDetailsDialogOpen}>
          <DialogContent className="max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <DialogTitle>
            </DialogTitle>
            {isDetailsDialogOpen && selectedPatientId && (
              <PatientDetails patientId={selectedPatientId} />
            )}
          </DialogContent>
          <Description>
          </Description>
        </Dialog>

        {crmAutoAppointmentPatientId ? (
          <div className="sr-only" aria-hidden>
            <AddAppointment
              key={crmAutoAppointmentPatientId}
              patientId={crmAutoAppointmentPatientId}
              initialOpen
              showTriggerButton={false}
              crmOpdBookingLeadId={crmOpdBookingLeadId}
              leadHospitalId={crmLeadHospitalId}
              onCrmOpdBookingComplete={() => {
                setCrmOpdBookingLeadId(null);
                setCrmLeadHospitalId(null);
              }}
              bookingHospitalId={user?.role === 'SUPER_ADMIN' ? user.hospitalId : undefined}
              initialRegistrationSource={crmAutoAppointmentSource?.registrationSource}
              initialRegistrationSourceDetails={crmAutoAppointmentSource?.registrationSourceDetails}
              onDialogClose={() => {
                setCrmAutoAppointmentPatientId(null);
                setCrmAutoAppointmentSource(null);
              }}
            />
          </div>
        ) : null}

        <CreateAdditionalServicesBill
          patient={additionalServicesPatient}
          isOpen={!!additionalServicesPatient}
          onClose={() => setAdditionalServicesPatient(null)}
        />
      </div>
    </div>
  );
}